/**
 * TaskStatusControl — the in-card status actions for reception.
 *
 * Offers the valid forward transitions (Start, Mark done, Cancel) for an active
 * task via `useUpdateTaskStatus`. Terminal tasks show no actions. Escalation is
 * NOT here — it is a separate, non-optimistic, confirmed flow (see EscalateDialog).
 */

import type { ServiceRequest } from '@hostesa/api-client';
import { useUpdateTaskStatus } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { Button } from '@hostesa/ui';
import { asOptionalString, isActive } from './task-presentation';

export interface TaskStatusControlProps {
  task: ServiceRequest;
  tenantId: string;
  hotelId: string;
}

export function TaskStatusControl({ task, tenantId, hotelId }: TaskStatusControlProps) {
  const { t } = useT('operations');
  const mutation = useUpdateTaskStatus(tenantId, hotelId);

  if (!isActive(task.status)) return null;

  const room = asOptionalString(task.room) ?? t('card.roomUnconfirmed');
  const category = t(`category.${task.category}`);
  const pending = mutation.isPending;

  const move = (to: 'in_progress' | 'done' | 'cancelled') => {
    mutation.mutate({ requestId: task.id, body: { to } });
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="task-status-control">
      {task.status !== 'in_progress' && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={pending}
          onClick={() => move('in_progress')}
          aria-label={`${t('actions.markInProgress')} — ${room}, ${category}`}
          data-testid={`task-start-btn-${task.id}`}
        >
          {pending ? t('actions.updating') : t('actions.markInProgress')}
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        variant="primary"
        disabled={pending}
        onClick={() => move('done')}
        aria-label={`${t('actions.markDone')} — ${room}, ${category}`}
        data-testid={`task-done-btn-${task.id}`}
      >
        {pending ? t('actions.updating') : t('actions.markDone')}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={pending}
        onClick={() => move('cancelled')}
        aria-label={`${t('actions.cancel')} — ${room}, ${category}`}
        data-testid={`task-cancel-btn-${task.id}`}
      >
        {t('actions.cancel')}
      </Button>
    </div>
  );
}
